<script setup lang="ts">
import { computed, ref } from "vue";
import Button from "@/components/Reusables/Buttons/Button.vue";
import Divider from "@/components/Reusables/Divider/Divider.vue";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import { useBillingDetails } from "../composables/useBillingDetails.composable";
import { BillingDetailsModalResult } from "../types/types";

const props = defineProps<{
  productLabel: string;
  priceEur: number | null;
}>();

const emit = defineEmits<{
  close: [result: BillingDetailsModalResult];
  cancel: [];
}>();

const { readBillingDetails, writeBillingDetails } = useBillingDetails();

const stored = readBillingDetails();

const companyName = ref(stored.companyName ?? "");
const vatNumber = ref(stored.vatNumber ?? "");
const address = ref(stored.address ?? "");
const postalCode = ref(stored.postalCode ?? "");
const city = ref(stored.city ?? "");
const country = ref(stored.country ?? "");
const email = ref(stored.email ?? "");

const formattedPrice = computed(() =>
  props.priceEur === null ? null : props.priceEur.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })
);

// Every field is optional server-side, so the only validation is a sanity check on a
// non-empty email — a typo there means the buyer's invoice carries a dead address.
const emailError = computed(() => {
  const value = email.value.trim();
  if (!value) return "";
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "" : "Enter a valid email address, or leave the field empty.";
});

const trimmedOrUndefined = (value: string) => {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
};

const continueToPayment = () => {
  if (emailError.value) return;

  // Persisted in the four-field form shape so the next purchase prefills exactly what was typed,
  // independent of how the API happens to want the address laid out.
  writeBillingDetails({
    companyName: trimmedOrUndefined(companyName.value),
    vatNumber: trimmedOrUndefined(vatNumber.value),
    address: trimmedOrUndefined(address.value),
    postalCode: trimmedOrUndefined(postalCode.value),
    city: trimmedOrUndefined(city.value),
    country: trimmedOrUndefined(country.value),
    email: trimmedOrUndefined(email.value),
  });

  emit("close", {
    billing: {
      companyName: trimmedOrUndefined(companyName.value),
      vatId: trimmedOrUndefined(vatNumber.value),
      streetAndNumber: trimmedOrUndefined(address.value),
      // Both blank collapses to "" and drops out; one-sided entry leaves a single token,
      // which is what the invoice should print anyway.
      postcodeAndCity: trimmedOrUndefined(`${postalCode.value.trim()} ${city.value.trim()}`),
      country: trimmedOrUndefined(country.value),
      email: trimmedOrUndefined(email.value),
    },
  });
};
</script>

<template>
  <div class="gap-px-24 flex flex-col">
    <div class="gap-px-8 flex flex-col">
      <h2 class="t-sh3 text-type-default">Invoicing details</h2>
      <p class="t-small text-type-muted">
        These details are printed on the invoice for this purchase and cannot be changed afterwards. All fields are optional —
        leave them blank to have the invoice made out to your organization's account name.
      </p>
    </div>

    <div class="p-px-16 rounded-8 gap-px-4 flex items-center justify-between bg-slate-100">
      <span class="t-small-bold text-type-default">{{ productLabel }}</span>
      <span
        v-if="formattedPrice"
        class="t-small text-type-muted">
        €{{ formattedPrice }}
      </span>
    </div>

    <div class="gap-px-16 flex flex-col">
      <TextInputNew
        v-model="companyName"
        label="Company name"
        placeholder="Byggefirma ApS" />

      <div class="gap-px-16 grid grid-cols-1 sm:grid-cols-2">
        <TextInputNew
          v-model="vatNumber"
          label="VAT number"
          placeholder="DK-12345678" />
        <TextInputNew
          v-model="email"
          label="Billing email"
          type="email"
          placeholder="accounting@company.com"
          :error-msg="emailError" />
      </div>

      <TextInputNew
        v-model="address"
        label="Address"
        placeholder="Byggevej 1" />

      <div class="gap-px-16 grid grid-cols-1 sm:grid-cols-3">
        <TextInputNew
          v-model="postalCode"
          label="Postal code"
          placeholder="8000" />
        <TextInputNew
          v-model="city"
          label="City"
          placeholder="Aarhus" />
        <TextInputNew
          v-model="country"
          label="Country"
          placeholder="Denmark" />
      </div>
    </div>

    <Divider spacing="none" />

    <div class="gap-px-8 flex justify-end">
      <Button
        label="Cancel"
        color="button"
        @click="emit('cancel')" />
      <Button
        label="Continue to payment"
        color="primary"
        :disabled="!!emailError"
        @click="continueToPayment" />
    </div>
  </div>
</template>
